import React, { useState, useRef, useEffect, useContext, ChangeEvent } from 'react';
import styled from 'styled-components';
import { VoteStorage, UserVote } from './VoteStorage';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import UserContext from '../../Contexts/User';
import WaitForResponse from '../Profile/CardTrade/WaitForResponse';
import VoteDetailModal from './VoteDetailModal';
import { showToast } from 'App';
import { ToastType } from 'Contexts/Notification';

// TypeScript interfaces for type safety
interface AddNewVoteProps {
  editingVote?: UserVote | null;
  onSave?: () => void;
  onCancel?: () => void;
}



interface ValidationErrors {
  file?: string;
  title?: string;
  category?: string;
  option1?: string;
  option2?: string;
  option1Icon?: string;
  option2Icon?: string;
  voteChoice?: string;
  productImage?: string;
  productName?: string;
  productLink?: string;
}

// Styled Components
const Container = styled.div`
  // min-height: 100vh;
  // background-color: #f9fafb;
  // padding: 2rem 1rem;
`;

const FormContainer = styled.div`
  max-width: 28rem;
  margin: 0 auto;
  background-color: white;
  border-radius: 0.5rem;
  // box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  padding: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: ${(props: { marginBottom?: string }) => props.marginBottom || '0'};
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const SmallLabel = styled(Label)`
  font-size: 0.75rem;
  color: #4b5563;
  margin-bottom: 0.25rem;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 0.375rem;
  outline: none;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: #3b82f6;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 0.375rem;
  outline: none;
  background-color: white;
  font-size: 0.875rem;
  color: #374151;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: #3b82f6;
  }
  
  option {
    padding: 0.5rem;
  }
`;

const OptionContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f9fafb;
  margin-bottom: 0.75rem;
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 0.875rem;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #111827;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background-color: #e5e7eb;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: #3b82f6;
  }
`;

const ErrorMessage = styled.p`
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #ef4444;
`;

const SuccessMessage = styled.p`
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #10b981;
`;

const SmallSuccessMessage = styled(SuccessMessage)`
  font-size: 0.75rem;
`;

const VoteOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const VoteOptionLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const RadioInput = styled.input`
  margin-right: 0.75rem;
  color: #2563eb;
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
`;

const OptionIcon = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  object-fit: cover;
  border-radius: 0.25rem;
  margin-right: 0.75rem;
`;

const OptionText = styled.span`
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`;

const SubmitButton = styled.button<{ $bg?: string; $hoverBg?: string }>`
  flex: 1;
  background-color: ${(p) => p.$bg ?? 'rgb(99, 82, 232)'};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: background-color 0.2s;
  outline: none;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: ${(p) => p.$hoverBg ?? (p.$bg ?? 'rgb(99, 82, 232)')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.8);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background-color: #6b7280;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: background-color 0.2s;
  outline: none;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: #4b5563;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.8);
  }
`;

const UploadVideoImg: React.FC<AddNewVoteProps> = ({ editingVote, onSave, onCancel }) => {
  // Get user context for Firebase functions
  const { user, userInfo } = useContext(UserContext);

  // Firebase functions
  const createDailyMatchup = httpsCallable(functions, 'createDailyMatchup');
  const editDailyMatchupById = httpsCallable(functions, 'editDailyMatchupById');
  const publishDailyMatchup = httpsCallable(functions, 'publishDailyMatchup');

  // Static categories list
  const categories = [
    { value: '', label: 'Select a category' },
    { value: 'food', label: 'Food & Dining' },
    { value: 'sport', label: 'Sports' },
    { value: 'tech', label: 'Technology' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'politics', label: 'Politics' },
    { value: 'business', label: 'Business' },
    { value: 'health', label: 'Health & Fitness' },
    { value: 'travel', label: 'Travel' },
    { value: 'education', label: 'Education' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'other', label: 'Other' }
  ];

  // State variables for form data
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [option1, setOption1] = useState<string>('');
  const [option2, setOption2] = useState<string>('');
  const [option1Icon, setOption1Icon] = useState<File | null>(null);
  const [option2Icon, setOption2Icon] = useState<File | null>(null);

  // Product fields
  const [productImage, setProductImage] = useState<File | null>(null);
  const [productImageUrl, setProductImageUrl] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [productLink, setProductLink] = useState<string>('');

  // Loading state for form submission
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewVote, setPreviewVote] = useState<UserVote | null>(null);

  // Utility function to create image URL from base64
  const createImageUrl = (base64Data: string): string => {
    if (base64Data.startsWith('data:')) {
      return base64Data; // Already a data URL
    }
    return `data:image/png;base64,${base64Data}`;
  };



  // State variables for validation and feedback
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Effect to populate form when editing
  useEffect(() => {
    if (editingVote) {
      setTitle(editingVote?.title);
      setOption1(editingVote?.option1);
      setOption2(editingVote?.option2);
      setCategory(editingVote?.category || "other");

      // Handle media file if exists
      if (editingVote.file) {
        setFile(editingVote.file);
      }
    }
  }, [editingVote]);
  console.log(editingVote, "editingVote")
  // Generic handler for all input changes
  const handleInputChange = (type: string, e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const value = e.target.value;
    const selectedFile = (e.target as HTMLInputElement).files?.[0] || null;

    // Handle different input types
    switch (type) {
      case 'file':
        if (selectedFile) {
          // Validate file size immediately
          const validation = VoteStorage.validateFileSize(selectedFile);
          if (!validation.isValid) {
            setErrors(prev => ({ ...prev, file: validation.error }));
            setFile(null);
            // Clear the file input
            e.target.value = '';
          } else {
            setFile(selectedFile);
            const reader = new FileReader();

            reader.onloadend = () => {
              if (reader.result && typeof reader.result === 'string') {
                const dataUrl = reader.result; // Full data URL: "data:image/png;base64,..."                                
                // setImageUrl(dataUrl);
                if (selectedFile.type.startsWith('image/')) {
                  setImageUrl(dataUrl);
                } else if (selectedFile.type.startsWith('video/')) {
                  setVideoUrl(dataUrl);
                }
              } else {
                console.error('Failed to read file as data URL');
                setErrors(prev => ({ ...prev, file: 'Failed to process the selected file' }));
              }
            };

            reader.onerror = () => {
              console.error('Error reading file:', reader.error);
              setErrors(prev => ({ ...prev, file: 'Error reading the selected file' }));
              setFile(null);
            };

            reader.readAsDataURL(selectedFile);
            if (errors.file) {
              setErrors(prev => ({ ...prev, file: undefined }));
            }
          }
        } else {
          setFile(null);
          if (errors.file) {
            setErrors(prev => ({ ...prev, file: undefined }));
          }
        }
        break;

      case 'title':
        setTitle(value);
        if (value.length >= 3 && errors.title) {
          setErrors(prev => ({ ...prev, title: undefined }));
        }
        break;

      case 'category':
        setCategory(value);
        if (value && errors.category) {
          setErrors(prev => ({ ...prev, category: undefined }));
        }
        break;

      case 'option1':
        setOption1(value);
        if (value.trim() && errors.option1) {
          setErrors(prev => ({ ...prev, option1: undefined }));
        }
        // Reset vote choice when options change        
        break;

      case 'option2':
        setOption2(value);
        if (value.trim() && errors.option2) {
          setErrors(prev => ({ ...prev, option2: undefined }));
        }
        // Reset vote choice when options change        
        break;

      case 'productImage':
        if (selectedFile) {
          // Validate file is an image
          if (!selectedFile.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, productImage: 'File must be an image' }));
            setProductImage(null);
            e.target.value = '';
          } else {
            setProductImage(selectedFile);
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result && typeof reader.result === 'string') {
                setProductImageUrl(reader.result);
              }
            };
            reader.onerror = () => {
              setErrors(prev => ({ ...prev, productImage: 'Error reading the image file' }));
              setProductImage(null);
            };
            reader.readAsDataURL(selectedFile);
            if (errors.productImage) {
              setErrors(prev => ({ ...prev, productImage: undefined }));
            }
          }
        } else {
          setProductImage(null);
          setProductImageUrl('');
          if (errors.productImage) {
            setErrors(prev => ({ ...prev, productImage: undefined }));
          }
        }
        break;

      case 'productName':
        setProductName(value);
        if (errors.productName) {
          setErrors(prev => ({ ...prev, productName: undefined }));
        }
        // Clear product link error if both are now filled
        if (value.trim() && productLink.trim() && errors.productLink) {
          setErrors(prev => ({ ...prev, productLink: undefined }));
        }
        break;

      case 'productLink':
        setProductLink(value);
        if (errors.productLink) {
          setErrors(prev => ({ ...prev, productLink: undefined }));
        }
        // Clear product name error if both are now filled
        if (value.trim() && productName.trim() && errors.productName) {
          setErrors(prev => ({ ...prev, productName: undefined }));
        }
        break;

      default:
        break;
    }
  };

  // Validate form fields and return boolean
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Validate file upload
    if (!file) {
      newErrors.file = 'Please upload a file';
    } else if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      newErrors.file = 'File must be an image or video';
    }

    // Validate title (optional, but if provided must be at least 3 characters)
    if (title.trim() && title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }

    // Validate category
    if (!category) {
      newErrors.category = 'Please select a category';
    }

    // Validate option 1
    if (!option1.trim()) {
      newErrors.option1 = 'First voting option is required';
    }

    // Validate option 2
    if (!option2.trim()) {
      newErrors.option2 = 'Second voting option is required';
    }

    // Validate product fields - both product_name and product_link must be filled together
    const hasProductName = productName.trim().length > 0;
    const hasProductLink = productLink.trim().length > 0;

    if (hasProductName && !hasProductLink) {
      newErrors.productLink = 'Product link is required when product name is provided';
    }

    if (hasProductLink && !hasProductName) {
      newErrors.productName = 'Product name is required when product link is provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (draft?: boolean): Promise<void> => {
    if (validateForm()) {
      setIsLoading(true); // Start loading
      try {
        // Check if user is authenticated
        if (!user?.uid) {
          setErrors({ title: 'User not authenticated' });
          setIsLoading(false); // Stop loading on error
          return;
        }

        // Prepare data for Firebase function
        const matchupData: any = {
          userId: user?.uid,
          vote: {
            slug: "minimum-wage-increase",
            category: category,
            headline: title.trim(),
            vote_type: "two_option",
            thumbs_meaning: {
              up: option1.trim(),
              down: option2.trim()
            },
            alt_text: title.trim(),
            media_type: file?.type?.includes('image') ? 'image' : 'video',
            image_prompt: imageUrl,
            video_prompt: videoUrl,
            isPublished: draft || false,
            // Add product fields if both are provided
            ...(productName.trim() && productLink.trim() && {
              product_name: productName.trim(),
              product_link: productLink.trim(),
              product_image: productImageUrl || null
            })
          }
        }

        if (editingVote) {
          // For updates, ensure we pass the existing matchup ID in edit mode
          if (editingVote?.id) {
            matchupData.id = editingVote.id;
          }
          const result = await editDailyMatchupById(matchupData);
          // Publish the vote if it's a draft
          if (draft) {
            await publishDailyMatchup({ id: editingVote.id });
          }
          console.log('Daily matchup updated:', result.data);
          setSuccessMessage('Vote updated successfully!');
        } else {
          // Create new daily matchup using Firebase function
          const result = await createDailyMatchup(matchupData);
          console.log('Daily matchup created:', result.data);
          setSuccessMessage('Vote created successfully!');
        }
        if (onSave) {
          onSave();
        }
        // Reset form after successful submission
        if (!editingVote) {
          setFile(null);
          setTitle('');
          setCategory('');
          setOption1('');
          setOption2('');
          setOption1Icon(null);
          setOption2Icon(null);
          setImageUrl(''); // Clear imageUrl
          setProductImage(null);
          setProductImageUrl('');
          setProductName('');
          setProductLink('');

          // Reset file inputs
          const fileInput = document.getElementById('file-input') as HTMLInputElement;
          const option1IconInput = document.getElementById('option1-icon') as HTMLInputElement;
          const option2IconInput = document.getElementById('option2-icon') as HTMLInputElement;
          const productImageInput = document.getElementById('product-image-input') as HTMLInputElement;

          if (fileInput) fileInput.value = '';
          if (option1IconInput) option1IconInput.value = '';
          if (option2IconInput) option2IconInput.value = '';
          if (productImageInput) productImageInput.value = '';
        }

        setErrors({});
        setIsLoading(false); // Stop loading on success

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);

      } catch (error: any) {
        console.error('Error saving vote:', error);

        // Handle specific error types with user-friendly messages
        if (error instanceof Error) {
          if (error.message.includes('size exceeds') || error.message.includes('limit')) {
            setErrors({ file: error.message });
          } else if (error.message.includes('Storage quota exceeded')) {
            setErrors({
              title: 'Storage quota exceeded. Please try uploading a smaller file or delete some existing votes to free up space.'
            });
          } else {
            showToast(error.message || 'Failed to save vote. Please try again.', ToastType.ERROR);
          }
        }
        setIsLoading(false); // Stop loading on error
      }
    }
  };

  const handleOpenPreview = (): void => {
    if (!validateForm()) return;
    const pv: UserVote = {
      id: 'preview',
      title: title.trim(),
      file: file,
      fileType: file?.type,
      imageUrl: imageUrl,
      videoUrl: videoUrl,
      media_type: file?.type?.includes('image') ? 'image' : 'video',
      category,
      option1: option1.trim(),
      option2: option2.trim(),
      option1Icon: '',
      option2Icon: '',
      status: 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      product_image: productImage || null,
      product_name: productName || '',
      product_link: productLink || '',
    };
    setPreviewVote(pv);
    setIsPreviewOpen(true);
  };

  const AccessEmail = ["menahemmaya@gmail.com", "Yanivb6@gmail.com", "sonisgnr.rj13@gmail.com"]

  return (
    <Container>
      {isLoading && <WaitForResponse />}
      <FormContainer>
        <Title>{editingVote ? 'Edit Voting Post' : 'Create Voting Post'}</Title>

        {/* Success Message */}
        {successMessage && (
          <SuccessMessage>{successMessage}</SuccessMessage>
        )}

        <FormSection>

          {/* Title Input Section */}
          <FormGroup>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => handleInputChange('title', e)}
              placeholder="Enter a title (optional, at least 3 characters)"
              hasError={!!errors.title}
            />
            {errors.title && (
              <ErrorMessage>{errors.title}</ErrorMessage>
            )}
          </FormGroup>

          {/* Category Selection */}
          <FormGroup>
            <Label htmlFor="category">Category *</Label>
            <Select
              id="category"
              value={category}
              onChange={(e) => handleInputChange('category', e)}
              hasError={!!errors.category}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </Select>
            {errors.category && (
              <ErrorMessage>{errors.category}</ErrorMessage>
            )}
          </FormGroup>

          {/* File Upload Section */}
          <FormGroup>
            <Label htmlFor="file-input">Upload Image or Video *</Label>
            <Input
              id="file-input"
              type="file"
              accept="image/*,video/*"
              onChange={(e) => handleInputChange('file', e)}
              hasError={!!errors.file}
            />
            {errors.file && (
              <ErrorMessage>{errors.file}</ErrorMessage>
            )}
            {file && (
              <SuccessMessage>
                Selected: {file.name} ({file.type})
              </SuccessMessage>
            )}
          </FormGroup>

          {/* Custom Vote Options Section */}
          <FormGroup>
            <Label>Create Voting Options *</Label>

            <VoteOptionsList>
              {/* Option 1 Input */}
              <OptionContainer>
                <SmallLabel htmlFor="option1">First Option</SmallLabel>
                <OptionRow>
                  <Input
                    id="option1"
                    type="text"
                    value={option1}
                    onChange={(e) => handleInputChange('option1', e)}
                    placeholder="e.g., Yes, Up, Right, Like, etc."
                    hasError={!!errors.option1}
                  />
                </OptionRow>
                {errors.option1 && (
                  <ErrorMessage>{errors.option1}</ErrorMessage>
                )}
              </OptionContainer>

              {/* Option 2 Input */}
              <OptionContainer>
                <SmallLabel htmlFor="option2">Second Option</SmallLabel>
                <OptionRow>
                  <Input
                    id="option2"
                    type="text"
                    value={option2}
                    onChange={(e) => handleInputChange('option2', e)}
                    placeholder="e.g., No, Down, Wrong, Dislike, etc."
                    hasError={!!errors.option2}
                  />
                </OptionRow>
                {errors.option2 && (
                  <ErrorMessage>{errors.option2}</ErrorMessage>
                )}
              </OptionContainer>
            </VoteOptionsList>
          </FormGroup>

          {/* Product Information Section (Optional) - Only for whitelisted users */}
          {userInfo?.email && AccessEmail.includes(userInfo?.email) &&
            <FormGroup>
              <Label>Product Information (Optional)</Label>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                If you add a product name, you must also add a product link
              </p>

              {/* Product Image Upload */}
              <FormGroup style={{ marginBottom: '0.75rem' }}>
                <SmallLabel htmlFor="product-image-input">Product Image(Optional)</SmallLabel>
                <Input
                  id="product-image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleInputChange('productImage', e)}
                  hasError={!!errors.productImage}
                />
                {errors.productImage && (
                  <ErrorMessage>{errors.productImage}</ErrorMessage>
                )}
                {productImage && (
                  <SmallSuccessMessage>
                    Selected: {productImage.name}
                  </SmallSuccessMessage>
                )}
              </FormGroup>

              {/* Product Name Input */}
              <FormGroup style={{ marginBottom: '0.75rem' }}>
                <SmallLabel htmlFor="product-name">Product Name</SmallLabel>
                <Input
                  id="product-name"
                  type="text"
                  value={productName}
                  onChange={(e) => handleInputChange('productName', e)}
                  placeholder="Enter product name"
                  hasError={!!errors.productName}
                />
                {errors.productName && (
                  <ErrorMessage>{errors.productName}</ErrorMessage>
                )}
              </FormGroup>

              {/* Product Link Input */}
              <FormGroup>
                <SmallLabel htmlFor="product-link">Product Link</SmallLabel>
                <Input
                  id="product-link"
                  type="url"
                  value={productLink}
                  onChange={(e) => handleInputChange('productLink', e)}
                  placeholder="Enter product link (URL)"
                  hasError={!!errors.productLink}
                />
                {errors.productLink && (
                  <ErrorMessage>{errors.productLink}</ErrorMessage>
                )}
              </FormGroup>
            </FormGroup>}

          {/* Submit Button */}
          <ButtonContainer>
            <SubmitButton type="button"
              onClick={handleOpenPreview}
              $bg={"#6352e8"}
              $hoverBg={"#6352e8"}>
              Preview Vote
            </SubmitButton>
            {(editingVote || onCancel) && (
              <CancelButton
                type="button"
                onClick={onCancel}
              >
                Cancel
              </CancelButton>
            )}
          </ButtonContainer>
        </FormSection>
      </FormContainer>
      <VoteDetailModal
        vote={previewVote}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onSubmitVote={() => { setIsPreviewOpen(false); handleSubmit(true); }}
        onAddDraft={() => { setIsPreviewOpen(false); handleSubmit(false); }}
      />
    </Container>
  );
};

export default UploadVideoImg;